"use client";

import {
  Alert,
  AlertTitle,
  Box,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from "@mui/material";

import { MDButton } from "@/components/atoms/MDButton";
import { IProductImage } from "@/interfaces/product-image.interface";
import { ISku } from "@/interfaces/sku.interface";
import { getPublicImageUrl } from "@/helpers/image-url-helper";

interface ImageDeletionDialogProps {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
  image: IProductImage | null;
  affectedSkus: ISku[];
  isLoading?: boolean;
  messages: {
    title: string;
    confirmButton: string;
    cancelButton: string;
    warningTitle: string;
    warningDescription: string;
    confirmDescription: string;
    mainImageLabel: string;
    affectedSkusLabel: string;
  };
}

/**
 * Dialog melhorado para deleção de imagens
 * Mostra preview da imagem e lista de SKUs afetados
 */
export const ImageDeletionDialog: React.FC<ImageDeletionDialogProps> = ({
  isOpen,
  onClose,
  onConfirm,
  image,
  affectedSkus,
  isLoading = false,
  messages,
}) => {
  if (!image) return null;

  const imageUrl = image.url || getPublicImageUrl(image.phisicalAddress) || "";
  const hasAffectedSkus = affectedSkus.length > 0;

  return (
    <Dialog open={isOpen} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{messages.title}</DialogTitle>
      <DialogContent>
        {/* Preview da imagem */}
        <Box display="flex" gap={2} mb={2} alignItems="flex-start">
          <Box
            component="img"
            src={imageUrl}
            alt={image.name}
            sx={{
              width: 100,
              height: 100,
              objectFit: "cover",
              borderRadius: 1,
              border: "1px solid",
              borderColor: "divider",
            }}
          />
          <Box flex={1}>
            <Typography variant="h6" gutterBottom>
              {image.name}
            </Typography>
            {image.main && (
              <Chip label={messages.mainImageLabel} color="primary" size="small" />
            )}
          </Box>
        </Box>

        {/* Alerta de SKUs afetados */}
        {hasAffectedSkus && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <AlertTitle>{messages.warningTitle}</AlertTitle>
            <Typography variant="body2" gutterBottom>
              {messages.warningDescription.replace('{count}', String(affectedSkus.length))}
            </Typography>
            <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
              {affectedSkus.slice(0, 5).map((sku) => (
                <li key={sku.code}>
                  <Typography variant="body2">
                    <strong>{sku.code}</strong> - {sku.title}
                  </Typography>
                </li>
              ))}
              {affectedSkus.length > 5 && (
                <li>
                  <Typography variant="body2" color="text.secondary">
                    ... e mais {affectedSkus.length - 5} SKU(s)
                  </Typography>
                </li>
              )}
            </Box>
          </Alert>
        )}

        {/* Mensagem de confirmação */}
        <Typography variant="body2" color="text.secondary">
          {messages.confirmDescription}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ p: 2 }}>
        <MDButton
          variant="outlined"
          color="dark"
          onClick={onClose}
          disabled={isLoading}
        >
          {messages.cancelButton}
        </MDButton>
        <MDButton
          variant="gradient"
          color="error"
          onClick={onConfirm}
          isLoading={isLoading}
          disabled={isLoading}
        >
          {messages.confirmButton}
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};
